import { computed, getCurrentInstance, onScopeDispose, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import type {
  IApplyReservationDepositDto,
  IChangeReservationStatusDto,
  ISeatReservationDto,
  IUpdateReservationDto,
} from '@/api/services/reservations.service';
import V2ReservationCancelModal from '@/components/modals/reservations/V2ReservationCancelModal.vue';
import type { IReservationCancelResult } from '@/interfaces/IReservationUi';
import V2ReservationTablePickerModal from '@/components/modals/reservations/V2ReservationTablePickerModal.vue';
import type { IReservationTablePickerResult } from '@/interfaces/IReservationUi';
import { useAuthStore } from '@/composition/use-auth';
import { useModal } from '@/composition/use-modal';
import { usePosMoney } from '@/composition/use-pos-money';
import { useUi } from '@/composition/use-ui';
import { getServerErrorCode } from '@/domain/reservations/reservation-errors';
import type { IStaffActionId } from '@/domain/reservations/reservation-status';
import { PermissionType } from '@/enums/PermissionType';
import { AlertType } from '@/interfaces/IAlert';
import type {
  IReservation,
  IReservationDetail,
} from '@/interfaces/IReservation';
import { useOrdersStore } from '@/stores/orders.store';
import { useReservationsStore } from '@/stores/reservations.store';
import { useRestaurantStore } from '@/stores/restaurant.store';

/** Both the POS projection and the complete staff row satisfy this contract. */
export type IReservationActionTarget = Pick<
  IReservation,
  | 'id'
  | 'status'
  | 'startsAt'
  | 'tableIds'
  | 'depositAmount'
  | 'depositStatus'
  | 'depositAppliedAt'
  | 'depositUnappliedAmount'
  | 'partySize'
> &
  Partial<Pick<IReservation, 'guestName' | 'tableSessionId' | 'seatedAt'>>;

export interface IReservationActionOptions {
  onUpdated?: (reservation: IReservation) => void;
  onStale?: () => void;
}

export function useReservationActions(
  reservationRef: Readonly<Ref<IReservationActionTarget | null | undefined>>,
  options: IReservationActionOptions = {}
) {
  const auth = useAuthStore();
  const restaurant = useRestaurantStore();
  const store = useReservationsStore();
  const orders = useOrdersStore();
  const modals = useModal();
  const ui = useUi();
  const money = usePosMoney();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const busy = ref(false);
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const canOrders = computed(() =>
    auth.permissionSet.has(PermissionType.OrdersManage)
  );
  let alive = true;
  let sequence = 0;
  watch(
    [
      () => reservationRef.value?.id,
      () => restaurant.restaurant?.id,
      () => restaurant.selectedBranch?.id,
    ],
    () => {
      sequence++;
      busy.value = false;
    },
    { flush: 'sync' }
  );
  onScopeDispose(() => {
    alive = false;
    sequence++;
  });

  function patch(updated: IReservation, current: () => boolean) {
    store.patchReservation(updated);
    if (current()) options.onUpdated?.(updated);
  }

  async function execute<T>(
    allowed: boolean,
    work: (
      row: IReservationActionTarget,
      current: () => boolean
    ) => Promise<T | null>,
    errorKey = 'reservations.action.statusError'
  ): Promise<T | null> {
    const row = reservationRef.value;
    if (!alive || !allowed || !row || busy.value) return null;
    const id = row.id;
    const branchId = restaurant.selectedBranch?.id;
    const restaurantId = restaurant.restaurant?.id;
    const request = ++sequence;
    const current = () =>
      alive &&
      request === sequence &&
      reservationRef.value?.id === id &&
      branchId === restaurant.selectedBranch?.id &&
      restaurantId === restaurant.restaurant?.id;
    busy.value = true;
    try {
      return await work(row, current);
    } catch (error) {
      if (!current()) return null;
      const code = getServerErrorCode(error);
      if (code === 'SESSION_HAS_ORDERS') {
        ui.showToast(AlertType.Info, t('reservations.action.undoSeatBlocked'));
        return null;
      }
      if (
        ['STALE', 'ILLEGAL_TRANSITION', 'DEPOSIT_ALREADY_PAID'].includes(
          code ?? ''
        ) &&
        canManage.value
      ) {
        ui.showToast(
          AlertType.Info,
          t(
            code === 'DEPOSIT_ALREADY_PAID'
              ? 'reservations.action.depositAlreadyPaid'
              : 'reservations.detail.conflict'
          )
        );
        try {
          const updated = await ReservationsService.getOne(id);
          patch(updated, current);
          if (current()) options.onStale?.();
        } catch (reloadError) {
          if (current())
            ui.serverError(reloadError, t('reservations.action.statusError'));
        }
      } else ui.serverError(error, t(errorKey));
      return null;
    } finally {
      if (current()) busy.value = false;
    }
  }

  function openTable(tableId = reservationRef.value?.tableIds[0]) {
    if (!canOrders.value || !tableId || !restaurant.restaurant) return;
    void proxy.$router.push({
      name: 'restaurantPosTable',
      params: {
        restaurantId: restaurant.restaurant.stringId,
        tableId: String(tableId),
      },
    });
  }

  async function chooseTables(row: IReservation, current: () => boolean) {
    if (!current()) return null;
    return modals.showDynamicDialog<IReservationTablePickerResult | null>(
      V2ReservationTablePickerModal,
      { reservation: row },
      'v2-reservation-tables',
      { width: 600, clickToClose: false }
    );
  }

  function seat(
    tableIds?: number[],
    seatOptions: { confirmOccupied?: boolean; announce?: boolean } = {}
  ) {
    return execute(canManage.value, async (row, current) => {
      let selected = tableIds;
      if (!selected && !row.tableIds.length) {
        const full = await ReservationsService.getOne(row.id);
        const result = await chooseTables(full, current);
        if (!result || !current()) return null;
        selected = result.tableIds;
      }
      if (!current()) return null;
      const dto: ISeatReservationDto = selected ? { tableIds: selected } : {};
      async function confirmOccupied() {
        const accepted = await modals.confirm({
          title: t('reservations.pos.seatOccupiedTitle'),
          message: t('reservations.pos.seatOccupiedMessage'),
          acceptText: t('reservations.action.seat'),
          cancelText: t('cancel'),
        });
        return accepted && current();
      }
      if (seatOptions.confirmOccupied) {
        if (!(await confirmOccupied())) return null;
        dto.attachToOccupied = true;
      }
      let result;
      try {
        result = await ReservationsService.seat(row.id, dto);
      } catch (error) {
        if (getServerErrorCode(error) !== 'TABLE_OCCUPIED' || !current())
          throw error;
        if (dto.attachToOccupied) throw error;
        if (!(await confirmOccupied())) return null;
        result = await ReservationsService.seat(row.id, {
          ...dto,
          attachToOccupied: true,
        });
      }
      patch(result.reservation, current);
      if (current() && seatOptions.announce !== false) {
        const table =
          result.reservation.tableNames.join(' + ') || String(result.tableId);
        ui.showToast(
          AlertType.Success,
          t('reservations.action.seated', { table }),
          canOrders.value
            ? {
                text: t('reservations.detail.openTable'),
                cb: () => openTable(result.tableId),
              }
            : undefined
        );
      }
      return result;
    });
  }

  function setTables(tableIds?: number[], force = false) {
    return execute(canManage.value, async (row, current) => {
      let selected = tableIds;
      let forced = force;
      if (!selected) {
        const full = await ReservationsService.getOne(row.id);
        const result = await chooseTables(full, current);
        if (!result || !current()) return null;
        selected = result.tableIds;
        forced = result.force;
      }
      if (!current()) return null;
      const result = await ReservationsService.setTables(row.id, {
        tableIds: selected,
        force: forced,
      });
      patch(result, current);
      if (current())
        ui.showToast(AlertType.Success, t('reservations.detail.saved'));
      return result;
    });
  }

  function update(dto: IUpdateReservationDto) {
    return execute(canManage.value, async (row, current) => {
      const result = await ReservationsService.update(row.id, dto);
      patch(result, current);
      if (current())
        ui.showToast(AlertType.Success, t('reservations.detail.saved'));
      return result;
    });
  }

  function resend(template: 'confirmed' | 'deposit_pending' = 'confirmed') {
    const payment = template === 'deposit_pending';
    return execute(
      canManage.value,
      async (row, current) => {
        const result = await ReservationsService.resend(row.id, template);
        if (current())
          ui.showToast(
            AlertType.Success,
            t(
              payment
                ? 'reservations.detail.resendPaymentSuccess'
                : 'reservations.detail.resent'
            )
          );
        return result;
      },
      payment
        ? 'reservations.detail.resendPaymentError'
        : 'reservations.detail.resendError'
    );
  }

  function refundDeposit(
    amount?: number,
    titleKey = amount === undefined
      ? 'reservations.deposit.retryRefund'
      : 'reservations.deposit.refundLeftover'
  ) {
    return execute(canManage.value, async (row, current) => {
      const accepted = await modals.confirm({
        title: t(titleKey),
        message: t('reservations.cancel.refundNote', {
          amount: money.formatComputedMoney(amount ?? row.depositAmount ?? 0),
        }),
        acceptText: t('confirm'),
        cancelText: t('cancel'),
      });
      if (!accepted || !current()) return null;
      const result = await ReservationsService.refundDeposit(row.id, amount);
      patch(result, current);
      // The API answers with the booking even when MercadoPago rejects the refund again.
      const failed = result.depositStatus === 'refund_failed';
      if (current())
        ui.showToast(
          failed ? AlertType.Error : AlertType.Success,
          t(
            failed
              ? 'reservations.deposit.refundStillFailed'
              : 'reservations.deposit.refundRetried'
          )
        );
      return result;
    });
  }

  function keepDeposit() {
    return execute(canManage.value, async (row, current) => {
      const accepted = await modals.confirm({
        title: t('reservations.deposit.keepLeftover'),
        message: t('reservations.deposit.leftover', {
          amount: money.formatComputedMoney(row.depositUnappliedAmount ?? 0),
        }),
        acceptText: t('confirm'),
        cancelText: t('cancel'),
      });
      if (!accepted || !current()) return null;
      const result = await ReservationsService.keepDeposit(row.id);
      patch(result, current);
      if (current())
        ui.showToast(AlertType.Success, t('reservations.deposit.leftoverKept'));
      return result;
    });
  }

  function waiveDeposit() {
    return execute(
      canManage.value && reservationRef.value?.status === 'pending_payment',
      async (row, current) => {
        const accepted = await modals.confirm({
          title: t('reservations.deposit.waiveTitle'),
          message: t('reservations.deposit.waiveMessage', {
            name: row.guestName ?? t('reservations.common.anonymous'),
          }),
          acceptText: t('reservations.deposit.waive'),
          cancelText: t('cancel'),
        });
        if (
          !accepted ||
          !current() ||
          !canManage.value ||
          reservationRef.value?.status !== 'pending_payment'
        )
          return null;
        const result = await ReservationsService.waiveDeposit(row.id);
        patch(result, current);
        if (current())
          ui.showToast(AlertType.Success, t('reservations.action.confirmed'));
        return result;
      }
    );
  }

  function applyDeposit(
    dto: IApplyReservationDepositDto = { cajaId: restaurant.caja?.id ?? null }
  ) {
    return execute(canOrders.value, async (row, current) => {
      const restaurantId = restaurant.restaurant?.id;
      const branchId = restaurant.selectedBranch?.id;
      if (!restaurantId || !branchId) return null;
      if (restaurant.selectedBranch?.settingUseCajas && !dto.cajaId) {
        ui.showToast(AlertType.Info, t('cajaV2.noCajaTitle'));
        return null;
      }
      const result = await ReservationsService.applyDeposit(row.id, dto);
      if ('restaurantId' in result.reservation)
        patch(result.reservation, current);
      else
        store.patchUpcomingReservation(
          result.reservation,
          restaurantId,
          branchId
        );
      if (current()) {
        for (const order of result.orders) orders.updateOrder(order.id, order);
        ui.showToast(
          AlertType.Success,
          t('reservations.pos.depositApplied', {
            amount: money.formatComputedMoney(
              (row.depositAmount ?? 0) - result.unapplied
            ),
          })
        );
      }
      return result;
    });
  }

  function reload(): Promise<IReservationDetail | null> {
    return execute(canManage.value, async (row, current) => {
      const result = await ReservationsService.getOne(row.id);
      patch(result, current);
      return current() ? result : null;
    });
  }

  function changeStatus(
    status: IReservation['status'],
    action: IStaffActionId
  ) {
    return execute(canManage.value, async (row, current) => {
      const dto: IChangeReservationStatusDto = { status };
      if (action === 'cancel') {
        const full = await ReservationsService.getOne(row.id);
        const settings =
          store.dayBranchId === full.branchId && store.day
            ? store.day.settingsSummary
            : (
                await ReservationsService.getDay(
                  full.restaurantId,
                  full.branchId,
                  full.serviceDate
                )
              ).settingsSummary;
        if (!current()) return null;
        const result = await modals.showDynamicDialog<IReservationCancelResult | null>(
          V2ReservationCancelModal,
          { reservation: full, refundHours: settings.depositRefundHours },
          'v2-reservation-cancel',
          { width: 480, clickToClose: false }
        );
        if (!result || !current()) return null;
        Object.assign(dto, result);
      } else if (action === 'noShow') {
        if (!(await confirmAbsence(row)) || !current()) return null;
      }
      if (!current()) return null;
      const result = await ReservationsService.setStatus(row.id, dto);
      patch(result, current);
      const messageKeys: Partial<Record<IReservation['status'], string>> = {
        confirmed: 'confirmed',
        arrived: 'arrived',
        cancelled: 'cancelled',
        finished: 'finished',
      };
      const key = messageKeys[status];
      if (current())
        ui.showToast(
          AlertType.Success,
          t(key ? `reservations.action.${key}` : 'reservations.detail.saved')
        );
      return result;
    });
  }

  function confirmAbsence(row: IReservation) {
    return modals.confirm({
      title: t('reservations.action.noShowTitle'),
      message:
        t('reservations.action.noShowMessage', {
          name: row.guestName ?? t('reservations.common.anonymous'),
        }) +
        (row.depositStatus === 'paid'
          ? ` ${t('reservations.action.noShowDepositNote')}`
          : ''),
      acceptText: t('reservations.action.noShow'),
      cancelText: t('cancel'),
    });
  }

  /** Confirms an automatic close-out as a real absence (forfeits a paid deposit). */
  function confirmNoShow() {
    return execute(canManage.value, async (row, current) => {
      if (!(await confirmAbsence(row)) || !current()) return null;
      const result = await ReservationsService.confirmNoShow(row.id);
      patch(result, current);
      if (current())
        ui.showToast(AlertType.Success, t('reservations.detail.saved'));
      return result;
    });
  }

  function run(action: IStaffActionId) {
    switch (action) {
      case 'seat':
        return seat();
      case 'confirmNoShow':
        return confirmNoShow();
      case 'attended':
        return changeStatus('finished', action);
      case 'openTable':
        openTable();
        return Promise.resolve(null);
      case 'resendPayment':
        return resend('deposit_pending');
      case 'waiveDeposit':
        return waiveDeposit();
      case 'retryRefund':
        return refundDeposit();
      case 'refundRemainder':
        return refundDeposit(
          reservationRef.value?.depositUnappliedAmount ?? undefined
        );
      case 'keepRemainder':
        return keepDeposit();
      case 'confirm':
        return changeStatus('confirmed', action);
      case 'arrive':
      case 'reopen':
      case 'undoSeat':
        return changeStatus('arrived', action);
      case 'noShow':
        return changeStatus('no_show', action);
      case 'cancel':
        return changeStatus('cancelled', action);
      case 'finish':
        return changeStatus('finished', action);
    }
  }
  return {
    busy,
    canManage,
    canOrders,
    run,
    seat,
    setTables,
    update,
    resend,
    applyDeposit,
    refundDeposit,
    keepDeposit,
    reload,
    openTable,
  };
}
